import { Button } from '../../components/Button';
import { ErrorText } from '../../components/ErrorText';
import { NotificationBox } from '../../components/NotificationBox';
import { PageHeading } from '../../components/PageHeading';
import { PageSection } from '../../components/Section';
import {
  ActionRow,
  ContentColumn,
  FormStack,
  IntroStack,
  Paragraph,
  ParagraphEmphasis,
} from './DeferredPaymentTemplate.styles';
import type {
  DeferredPaymentTemplateLabels,
  DeferredPaymentTemplateProps,
} from './DeferredPaymentTemplate.types';

const DEFAULT_LABELS: Required<DeferredPaymentTemplateLabels> = {
  title: 'Make a payment',
  cardDetailsHeading: 'Please enter your card details below to complete the payment.',
  submit: 'Complete order',
  confirmationTitle: 'Payment complete',
  notFoundTitle: 'Payment not found',
  returnHome: 'Return to homepage',
};

export function DeferredPaymentTemplate({
  greeting,
  message,
  paymentSlot,
  onSubmit,
  isSubmitting = false,
  error,
  successMessage,
  successNote,
  notFoundMessage,
  onReturnHome,
  labels,
}: DeferredPaymentTemplateProps) {
  const merged = { ...DEFAULT_LABELS, ...labels };

  const returnHomeCta = onReturnHome && (
    <Button type="button" variant="primary" onClick={onReturnHome}>
      {merged.returnHome}
    </Button>
  );

  if (notFoundMessage) {
    return (
      <PageSection>
        <ContentColumn>
          <PageHeading title={merged.notFoundTitle} />
          <NotificationBox title={notFoundMessage} cta={returnHomeCta} />
        </ContentColumn>
      </PageSection>
    );
  }

  if (successMessage) {
    return (
      <PageSection>
        <ContentColumn>
          <PageHeading title={merged.confirmationTitle} />
          <NotificationBox title={successMessage} subtitle={successNote} cta={returnHomeCta} />
        </ContentColumn>
      </PageSection>
    );
  }

  const handleSubmit = () => {
    if (isSubmitting) return;
    onSubmit?.();
  };

  return (
    <PageSection>
      <ContentColumn>
        <PageHeading title={merged.title} />
        <FormStack>
          <IntroStack>
            {greeting && <Paragraph>{greeting}</Paragraph>}
            {message && <Paragraph>{message}</Paragraph>}
            <ParagraphEmphasis>{merged.cardDetailsHeading}</ParagraphEmphasis>
          </IntroStack>
          {paymentSlot}
          {error && <ErrorText role="alert">{error}</ErrorText>}
          <ActionRow>
            <Button
              type="button"
              variant="primary"
              fullWidth
              loading={isSubmitting}
              onClick={handleSubmit}
            >
              {merged.submit}
            </Button>
          </ActionRow>
        </FormStack>
      </ContentColumn>
    </PageSection>
  );
}
